
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { seasonService } from "../../../../../Services";

interface Cluster {
  value: string;
  label: string;
}

interface ClusterContextType {
  stateCluster: Cluster[];
  loading: boolean;
  error: Error | null;
  fetchClustersData: (seasonId: string, stateId: string, query?: number) => Promise<void>;
  clearClusters: () => void;
  refreshClusters: (seasonId?: string, stateId?: string) => Promise<void>;
  currentSeasonId: string | null;
  currentStateId: string | null;
  setCurrentIds: (seasonId: string, stateId: string) => void;
}

interface ClusterProviderProps {
  children: ReactNode;
}

interface ClusterCache {
  [seasonId: string]: {
    [stateId: string]: {
      clusters: Cluster[];
      timestamp: number;
    };
  } | undefined;
}

const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes cache expiry

const ClusterContext = createContext<ClusterContextType | undefined>(undefined);

export const ClusterProvider: React.FC<ClusterProviderProps> = ({ children }) => {
  const [stateCluster, setStateCluster] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [clusterCache, setClusterCache] = useState<ClusterCache>({});
  const [currentSeasonId, setCurrentSeasonId] = useState<string | null>(null);
  const [currentStateId, setCurrentStateId] = useState<string | null>(null);

  // Store seasonId and stateId for later use
  const setCurrentIds = (seasonId: string, stateId: string) => {
    setCurrentSeasonId(seasonId);
    setCurrentStateId(stateId);
    
    // We can also store these in localStorage for persistence across page refreshes
    localStorage.setItem('currentSeasonId', seasonId);
    localStorage.setItem('currentStateId', stateId);
  };

  // On initial load, try to restore from localStorage
  useEffect(() => {
    const savedSeasonId = localStorage.getItem('currentSeasonId');
    const savedStateId = localStorage.getItem('currentStateId');
    
    if (savedSeasonId && savedStateId) {
      setCurrentSeasonId(savedSeasonId);
      setCurrentStateId(savedStateId);
      
      // Optionally auto-fetch data if none exists
      if (stateCluster.length === 0) {
        fetchClustersData(savedSeasonId, savedStateId);
      }
    }
  }, []);

  const getclustersWithState = async (seasonId: string, stateId: string, query: number = 1) => {
    try {
      const fields = await seasonService.getAllField(seasonId);
      const requestData = {
        pageNumber: query,
        pageSize: 20,
        seasonId: seasonId,
        stateId: stateId,
        clusterColumns: [...fields.data.data],
      };
      const response = await seasonService.getAllStateCluster(requestData);
      if (Array.isArray(response.data.data.clusterData.data)) {
        return response.data.data.clusterData.data.map((cluster: any) => ({
          value: cluster.uniqueId,
          label: cluster.clustername,
        }));
      }
    } catch (error) {
      console.error("Error fetching clusters:", error);
      throw error;
    }
    return [];
  };

  const getCachedClusters = (seasonId: string, stateId: string): Cluster[] | null => {
    // First check if we already have the data in state for the SAME seasonId and stateId
    if (stateCluster.length > 0 && currentSeasonId === seasonId && currentStateId === stateId) {
      return stateCluster;
    }

    // Then check cache
    const seasonData = clusterCache[seasonId];
    if (seasonData && seasonData[stateId]) {
      const cacheEntry = seasonData[stateId];
      if (cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_EXPIRY_MS) {
        return cacheEntry.clusters;
      }
    }
    return null;
  };

  const updateClusterCache = (seasonId: string, stateId: string, clusters: Cluster[]) => {
    setClusterCache(prevCache => ({
      ...prevCache,
      [seasonId]: {
        ...(prevCache[seasonId] || {}),
        [stateId]: {
          clusters,
          timestamp: Date.now()
        }
      }
    }));
  };

  const fetchClustersData = async (seasonId: string, stateId: string, query: number = 1) => {
    if (!stateId || !seasonId) {
      console.error("Both seasonId and stateId are required to fetch clusters");
      return;
    }

    // Update current IDs
    setCurrentIds(seasonId, stateId);
    setLoading(true);
    setError(null);
    
    try {
      // Check for cached data first
      const cachedClusters = getCachedClusters(seasonId, stateId);
      if (cachedClusters) {
        console.log("Using cached cluster data for state:", stateId);
        setStateCluster(cachedClusters);
        setLoading(false);
        return;
      }

      // Fetch fresh data
      console.log(`Fetching clusters for state: ${stateId} with season: ${seasonId}`);
      const clusters = await getclustersWithState(seasonId, stateId, query);
      
      // Update state and cache
      setStateCluster(clusters);
      updateClusterCache(seasonId, stateId, clusters);
    } catch (err) {
      console.error("Error in fetchClustersData:", err);
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
      setStateCluster([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshClusters = async (seasonId?: string, stateId?: string) => {
    // Use provided IDs or fall back to stored IDs
    const sId = seasonId || currentSeasonId;
    const stId = stateId || currentStateId;
    
    if (!sId || !stId) {
      console.error("Cannot refresh clusters without seasonId and stateId");
      return;
    }
    
    try {
      setLoading(true);
      const clusters = await getclustersWithState(sId, stId);
      
      setStateCluster(clusters);
      updateClusterCache(sId, stId, clusters);
    } catch (err) {
      console.error("Error refreshing clusters:", err);
      setError(err instanceof Error ? err : new Error('Failed to refresh clusters'));
    } finally {
      setLoading(false);
    }
  };

  const clearClusters = () => {
    setStateCluster([]);
  };

  const value: ClusterContextType = {
    stateCluster,
    loading,
    error,
    fetchClustersData,
    clearClusters,
    refreshClusters,
    currentSeasonId,
    currentStateId,
    setCurrentIds
  };

  return <ClusterContext.Provider value={value}>{children}</ClusterContext.Provider>;
};

export const useCluster = () => {
  const context = useContext(ClusterContext);
  if (context === undefined) {
    throw new Error('useCluster must be used within a ClusterProvider');
  }
  return context;
};